import { useMemo, useState } from "react";
import { X } from "lucide-react";
import { Alert, Banner, Button, Drawer, Field, FilterChip, Icon, Input, SearchSelect, TextArea, Toggle, type SearchSelectOption } from "@/shared/rd";
import type { FeatureFlagView } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useCreateFeatureFlagMutation } from "../services/useCreateFeatureFlagMutation";
import { useUpdateFeatureFlagMutation } from "../services/useUpdateFeatureFlagMutation";
import { useStoreOptionsQuery } from "../services/useStoreOptionsQuery";

/** closed, creating, or editing one flag. */
export type FlagFormTarget = { mode: "closed" } | { mode: "create" } | { mode: "edit"; flag: FeatureFlagView };

type Props = {
  target: FlagFormTarget;
  onClose: () => void;
  /** After a save, with the past-tense confirmation for the page's toast. */
  onSaved: (message: string) => void;
};

type Draft = {
  key: string;
  nameEn: string;
  nameAr: string;
  descriptionEn: string;
  descriptionAr: string;
  isActive: boolean;
  enableForAllStores: boolean;
  storeIds: string[];
};

const FORM_ID = "feature-flag-form";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

function draftFromFlag(flag: FeatureFlagView | undefined): Draft {
  if (!flag) {
    return {
      key: "",
      nameEn: "",
      nameAr: "",
      descriptionEn: "",
      descriptionAr: "",
      isActive: true,
      enableForAllStores: false,
      storeIds: [],
    };
  }
  return {
    key: flag.key,
    nameEn: flag.nameEn,
    nameAr: flag.nameAr ?? "",
    descriptionEn: flag.descriptionEn ?? "",
    descriptionAr: flag.descriptionAr ?? "",
    isActive: flag.isActive,
    enableForAllStores: flag.enableForAllStores,
    storeIds: flag.targetStores.map((store) => store.id),
  };
}

/**
 * Create or edit a flag in a drawer over the list. The key is fixed once a
 * flag exists. Targeting is the store allow-list as chips, filtered by a
 * search box, fed by the admin stores endpoint (see useStoreOptionsQuery).
 */
export function FeatureFlagFormDialog({ target, onClose, onSaved }: Props) {
  const t = useTranslations();
  const flag = target.mode === "edit" ? target.flag : undefined;
  const isEdit = Boolean(flag);
  const create = useCreateFeatureFlagMutation();
  const update = useUpdateFeatureFlagMutation();
  const isPending = create.isPending || update.isPending;
  return (
    <Drawer
      open={target.mode !== "closed"}
      onClose={onClose}
      closeLabel={t(keys.common.close)}
      width={520}
      badge="flag"
      crumb={`${t(keys.featureFlags.title)} / ${flag ? flag.key : t("featureFlags.newCrumb")}`}
      title={isEdit ? t(keys.featureFlags.editTitle) : t(keys.featureFlags.addTitle)}
      actions={
        <Button
          type="submit"
          form={FORM_ID}
          variant="primary"
          size="sm"
          loading={isPending}
          loadingLabel={isEdit ? t("featureFlags.saving") : t("featureFlags.creating")}
        >
          {isEdit ? t(keys.common.save) : t(keys.common.create)}
        </Button>
      }
    >
      {/* Keyed so the draft re-seeds whenever the target flag changes. */}
      <FlagForm key={flag?.id ?? "create"} flag={flag} onSaved={onSaved} create={create} update={update} />
    </Drawer>
  );
}

function FlagForm({
  flag,
  onSaved,
  create,
  update,
}: {
  flag?: FeatureFlagView;
  onSaved: (message: string) => void;
  /** Owned by the drawer, so its bar's button can show the request running. */
  create: ReturnType<typeof useCreateFeatureFlagMutation>;
  update: ReturnType<typeof useUpdateFeatureFlagMutation>;
}) {
  const t = useTranslations();
  const isEdit = Boolean(flag);
  const [draft, setDraft] = useState<Draft>(() => draftFromFlag(flag));
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);

  // Only load the (potentially large) store list while the picker is relevant.
  const storeOptions = useStoreOptionsQuery(!draft.enableForAllStores);
  // Every store we can name: the loaded page plus any targeted store beyond it,
  // so a chosen store always shows as a chip and can be removed.
  const knownStores = useMemo(() => {
    const byId = new Map<string, { label: string; storeSlug: string; deactivated: boolean }>();
    for (const store of flag?.targetStores ?? []) {
      byId.set(store.id, { label: store.storeName, storeSlug: store.storeSlug, deactivated: false });
    }
    for (const option of storeOptions.options) byId.set(option.value, option);
    return byId;
  }, [storeOptions.options, flag]);
  const storeLabel = (id: string) => {
    const store = knownStores.get(id);
    if (!store) return id;
    return store.deactivated ? `${store.label} (${t(keys.storeControls.statusDeactivated)})` : store.label;
  };
  // The picker offers only stores not chosen yet; picking one adds it.
  const pickable = useMemo<SearchSelectOption[]>(
    () =>
      storeOptions.options
        .filter((option) => !draft.storeIds.includes(option.value))
        .map((option) => ({
          value: option.value,
          label: option.deactivated ? `${option.label} (${t(keys.storeControls.statusDeactivated)})` : option.label,
          detail: option.storeSlug,
        })),
    [storeOptions.options, draft.storeIds, t],
  );

  const isPending = create.isPending || update.isPending;
  const set = <K extends keyof Draft>(field: K, value: Draft[K]) => setDraft((prev) => ({ ...prev, [field]: value }));
  const addStore = (id: string) =>
    setDraft((prev) => (prev.storeIds.includes(id) ? prev : { ...prev, storeIds: [...prev.storeIds, id] }));
  const removeStore = (id: string) => setDraft((prev) => ({ ...prev, storeIds: prev.storeIds.filter((s) => s !== id) }));

  const required = t(keys.errors.required);
  const missing = {
    key: !isEdit && draft.key.trim().length === 0,
    nameEn: draft.nameEn.trim().length === 0,
    stores: !draft.enableForAllStores && draft.storeIds.length === 0,
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isPending) return;
    setTried(true);
    if (missing.key || missing.nameEn || missing.stores) {
      setError(null);
      return;
    }
    try {
      if (isEdit && flag) {
        await update.mutateAsync({
          id: flag.id,
          nameEn: draft.nameEn.trim(),
          nameAr: draft.nameAr.trim(),
          descriptionEn: draft.descriptionEn.trim(),
          descriptionAr: draft.descriptionAr.trim(),
          isActive: draft.isActive,
          enableForAllStores: draft.enableForAllStores,
          // When "all stores" is on, omit storeIds so the curated allow-list is
          // preserved (and restored if the admin toggles back). Otherwise send
          // the picked stores — the backend replaces the list wholesale.
          ...(draft.enableForAllStores ? {} : { storeIds: draft.storeIds }),
        });
      } else {
        await create.mutateAsync({
          key: draft.key.trim(),
          nameEn: draft.nameEn.trim(),
          nameAr: draft.nameAr.trim(),
          descriptionEn: draft.descriptionEn.trim(),
          descriptionAr: draft.descriptionAr.trim(),
          isActive: draft.isActive,
          enableForAllStores: draft.enableForAllStores,
          storeIds: draft.enableForAllStores ? [] : draft.storeIds,
        });
      }
      const name = draft.nameEn.trim();
      onSaved(isEdit ? t("featureFlags.toastSavedNamed", { name }) : t("featureFlags.toastCreatedNamed", { name }));
    } catch (err) {
      setError(err instanceof ApiJsonClientError ? err.message || t(keys.errors.generic) : t(keys.errors.generic));
    }
  };

  const showTruncatedHint = storeOptions.total > storeOptions.loadedCount;

  return (
    <form id={FORM_ID} onSubmit={handleSubmit} noValidate aria-busy={isPending || undefined} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {error ? (
        <Banner
          tone="error"
          title={isEdit ? t("featureFlags.saveFailedTitle") : t("featureFlags.createFailedTitle")}
          actions={
            <Button type="submit" size="sm" loading={isPending} loadingLabel={t(keys.shell.retrying)}>
              {t(keys.shell.retry)}
            </Button>
          }
        >
          {`${error} ${t("featureFlags.nothingChangedForm")}`}
        </Banner>
      ) : null}
      <Field
        label={t(keys.featureFlags.formKeyLabel)}
        help={t(keys.featureFlags.formKeyHelper)}
        error={tried && missing.key ? required : undefined}
        htmlFor="flag-key"
      >
        <Input
          id="flag-key"
          name="key"
          dir="ltr"
          autoFocus={!isEdit}
          disabled={isEdit}
          placeholder="NEW_SELLER_DASHBOARD"
          value={draft.key}
          invalid={tried && missing.key}
          onChange={(event) => set("key", event.target.value.toUpperCase().replace(/\s+/g, "_"))}
          style={{ fontFamily: MONO }}
        />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))", gap: 12 }}>
        <Field label={t(keys.featureFlags.formNameEnLabel)} error={tried && missing.nameEn ? required : undefined} htmlFor="flag-name-en">
          <Input id="flag-name-en" name="nameEn" dir="ltr" value={draft.nameEn} invalid={tried && missing.nameEn} onChange={(event) => set("nameEn", event.target.value)} />
        </Field>
        <Field label={`${t(keys.featureFlags.formNameArLabel)} ${t("featureFlags.optionalSuffix")}`} htmlFor="flag-name-ar">
          <Input id="flag-name-ar" name="nameAr" dir="rtl" value={draft.nameAr} onChange={(event) => set("nameAr", event.target.value)} />
        </Field>
      </div>
      <Field label={`${t(keys.featureFlags.formDescriptionEnLabel)} ${t("featureFlags.optionalSuffix")}`} htmlFor="flag-desc-en">
        <TextArea id="flag-desc-en" name="descriptionEn" dir="ltr" rows={2} value={draft.descriptionEn} onChange={(event) => set("descriptionEn", event.target.value)} />
      </Field>
      <Field label={`${t(keys.featureFlags.formDescriptionArLabel)} ${t("featureFlags.optionalSuffix")}`} htmlFor="flag-desc-ar">
        <TextArea id="flag-desc-ar" name="descriptionAr" dir="rtl" rows={2} value={draft.descriptionAr} onChange={(event) => set("descriptionAr", event.target.value)} />
      </Field>

      <ToggleRow
        label={t(keys.featureFlags.formActiveLabel)}
        help={t(keys.featureFlags.formActiveHelper)}
        checked={draft.isActive}
        onChange={(on) => set("isActive", on)}
      />
      <ToggleRow
        label={t(keys.featureFlags.formAllStoresLabel)}
        help={t(keys.featureFlags.formAllStoresHelper)}
        checked={draft.enableForAllStores}
        onChange={(on) => set("enableForAllStores", on)}
      />

      {!draft.enableForAllStores ? (
        <Field
          label={t(keys.featureFlags.formStoresLabel)}
          help={showTruncatedHint ? `${t(keys.featureFlags.formStoresHelper)} ${t(keys.featureFlags.formStoresTruncatedHint, { loaded: storeOptions.loadedCount, total: storeOptions.total })}` : t(keys.featureFlags.formStoresHelper)}
          error={tried && missing.stores ? required : undefined}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {storeOptions.error ? (
              <Alert
                tone="error"
                action={
                  <Button variant="ghost" size="sm" onClick={() => void storeOptions.refetch()}>
                    {t(keys.common.retry)}
                  </Button>
                }
              >
                {t(keys.common.loadFailed)}
              </Alert>
            ) : (
              <SearchSelect
                id="flag-stores"
                label={t(keys.featureFlags.formStoresLabel)}
                placeholder={storeOptions.isLoading ? t(keys.common.loading) : t(keys.featureFlags.formStoresSearchPlaceholder)}
                emptyLabel={t(keys.featureFlags.formStoresNoResults)}
                options={pickable}
                onChange={addStore}
                disabled={storeOptions.isLoading}
              />
            )}
            {draft.storeIds.length > 0 ? (
              <div role="group" aria-label={t(keys.featureFlags.formStoresLabel)} style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {draft.storeIds.map((id) => (
                  <FilterChip
                    key={id}
                    active
                    aria-label={`${t(keys.common.remove)}: ${storeLabel(id)}`}
                    onClick={() => removeStore(id)}
                  >
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      {storeLabel(id)}
                      <Icon icon={X} size={13} />
                    </span>
                  </FilterChip>
                ))}
              </div>
            ) : null}
          </div>
        </Field>
      ) : null}

    </form>
  );
}

/** A switch with its label and a line on what it does. */
function ToggleRow({ label, help, checked, onChange }: { label: string; help: string; checked: boolean; onChange: (on: boolean) => void }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
      <Toggle checked={checked} onChange={onChange} label={label} />
      <span style={{ display: "flex", flexDirection: "column" }}>
        <span style={{ fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: 12, color: "var(--rd-text-meta)" }}>{help}</span>
      </span>
    </div>
  );
}
